import TabPanel from '@components/TabPanel'
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  ListItemText,
  Paper,
  Popover,
  Stack,
  Tab,
  Tabs,
  Typography,
  capitalize,
  useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { HiChevronDoubleRight, HiLockOpen, HiCodeBracket } from 'react-icons/hi2'
import { executeAndSetInterval } from '@plug-core/utils/executeAndSetInterval'
import { HiChevronDoubleLeft } from 'react-icons/hi'
import { CiWarning } from 'react-icons/ci'
import { useTranslation } from 'react-i18next'
import {
  getTransactionType,
  callContractFunction,
  calculateTransactionFee,
  getGasLimit,
  getGasData,
  truncateEthAddress,
  fetchTokenData,
  chooseSymbol,
} from '@plug-core/utils/eth'
import { logger } from '@plug-core/logger'
import { formatNumber } from '@plug-core/utils/formatNumber'

import {
  TransactionType,
  type GasData,
  type TokenData,
  type ContractInfo,
} from '@plug-core/interfaces/eth'
import { Chain } from '@plug-core/interfaces/common'

const FEE_REFRESH_INTERVAL = 10000

interface EthSendTransactionParams {
  transfer: {
    from?: string
    to: string
    value?: string
    data?: string
    gas?: string
    gasPrice?: string
    gasLimit?: string
    maxFeePerGas?: string
    maxPriorityFeePerGas?: string
    chainId?: string
  }
}

const TransferEth = ({ transfer }: EthSendTransactionParams) => {
  const [currentTab, setCurrentTab] = useState(0)
  const [gasCachedData, setGasCachedData] = useState<GasData>()
  const [tokenData, setTokenData] = useState<TokenData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [fee, setFee] = useState<string>('0')
  const [contractWarning, setContractWarning] = useState('')
  const [gasWarning, setGasWarning] = useState('')
  const [contractInformation, setContractInfomation] = useState<ContractInfo>()
  const { palette } = useTheme()
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  let type = getTransactionType(transfer.data)

  useEffect(() => {
    const fetchGasLimit = async () => {
      try {
        const fetchedContractInfo = await callContractFunction(
          transfer.to,
          transfer.data,
          transfer.value,
          transfer.from,
          Chain.ETH // TODO: when fixing eth sign in, add dynamic chain
        )

        if (!contractInformation) {
          if (fetchedContractInfo) {
            setContractInfomation(fetchedContractInfo)
          } else if (type === TransactionType.CONTRACT_CALL) {
            setContractWarning(t('transferEth.warning'))
          }
        }

        if (
          (transfer.gas || transfer.gasLimit) &&
          transfer.maxFeePerGas &&
          transfer.maxPriorityFeePerGas
        ) {
          const calculatedFee = calculateTransactionFee(
            transfer.gas ? BigInt(transfer.gas) : BigInt(transfer.gasLimit || '0'),
            BigInt(transfer.maxFeePerGas),
            BigInt(transfer.maxPriorityFeePerGas)
          )
          setFee(calculatedFee)
        } else if (gasCachedData) {
          if (!fetchedContractInfo?.gasLimit && type === TransactionType.CONTRACT_CALL) {
            setGasWarning(t('transferEth.gasWarning'))
          }
          const calculatedFee = calculateTransactionFee(
            fetchedContractInfo?.gasLimit || getGasLimit(gasCachedData, type),
            BigInt(gasCachedData.maxFeePerGas),
            BigInt(gasCachedData.maxPriorityFeePerGas)
          )
          setFee(calculatedFee)
        }
      } catch (error) {
        setContractWarning(t('transferEth.warning'))
        setFee('0')
        logger.error('Error fetching gas', { originalError: error })
      }
    }

    fetchGasLimit()
  }, [gasCachedData])

  useEffect(() => {
    if (
      (transfer.gas || transfer.gasLimit) &&
      transfer.maxFeePerGas &&
      transfer.maxPriorityFeePerGas
    ) {
      return
    }

    const interval = executeAndSetInterval(async () => {
      setIsLoading(true)
      try {
        const data = await getGasData()
        setGasCachedData(data)
      } catch (error) {
        logger.error('Failed to get gas data', { originalError: error })
      } finally {
        setIsLoading(false)
      }
    }, FEE_REFRESH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [transfer.gasLimit, transfer.gas, transfer.maxFeePerGas, transfer.maxPriorityFeePerGas])

  useEffect(() => {
    fetchTokenData(transfer.data, transfer.to, transfer.value, type).then(setTokenData)
  }, [transfer.data])

  if (!tokenData) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 200 }}>
        <CircularProgress />
        <Typography color="text.secondary" variant="body2">
          {t('transferEth.loading')}
        </Typography>
      </Stack>
    )
  }

  const { decimals, logo, name, symbol, to, value } = tokenData

  const renderArrowView = () => {
    return (
      <Stack
        position="absolute"
        justifyContent="center"
        p={1}
        bgcolor={(theme) => theme.palette.background.default}
        borderRadius={10}
      >
        {type === TransactionType.APPROVE ? (
          <HiLockOpen color={palette.primary.main} size={30} />
        ) : type === TransactionType.DEPOSIT ? (
          <HiChevronDoubleLeft size={30} color={palette.primary.main} />
        ) : type === TransactionType.CONTRACT_CALL ? (
          <HiCodeBracket color={palette.primary.main} size={30} />
        ) : (
          <HiChevronDoubleRight size={30} color={palette.primary.main} />
        )}
      </Stack>
    )
  }

  const renderPropertyRow = (title: string, value: string) => (
    <Stack spacing={1}>
      <Typography variant="body2">{title}</Typography>
      <Stack
        bgcolor={(theme) => theme.palette.background.paper}
        sx={{ borderRadius: 1 }}
        px={2}
        py={1}
        minHeight={20}
      >
        <Typography sx={{ overflowWrap: 'break-word' }}>{value}</Typography>
      </Stack>
    </Stack>
  )

  return (
    <Stack flex={1}>
      <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
        <Tab sx={{ flex: 1 }} label={t('transferEth.info')} />
        <Tab sx={{ flex: 1 }} label={t('transferEth.params.title')} />
      </Tabs>
      {contractWarning && (
        <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
          {contractWarning}
        </Alert>
      )}
      <TabPanel value={currentTab} index={0}>
        <Stack flex={1} justifyContent="space-between">
          <Stack direction="row" justifyContent="center" alignItems="center" mb={3.5}>
            <Stack component={Paper} alignItems="center" spacing={1.5} p={2} ml={1} flex={1}>
              <Avatar src={logo} />
              <Stack spacing={0.5}>
                <Typography variant="caption" textAlign="center">
                  {symbol}
                </Typography>
              </Stack>
            </Stack>
            {renderArrowView()}
            <Stack flex={1} component={Paper} alignItems="center" spacing={1.5} p={2} mx={1}>
              <Avatar>😇</Avatar>
              <Stack spacing={0.5}>
                <Typography variant="caption" textAlign="center">
                  {truncateEthAddress(to)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Stack px={2} spacing={0.5}>
            <Stack direction="row">
              <ListItemText primary={t('transferEth.params.transferAmount')} />
              <ListItemText
                primaryTypographyProps={{
                  textAlign: 'right',
                  color: (theme) => theme.palette.rainbow.blue,
                  fontSize: 23,
                }}
                secondaryTypographyProps={{
                  textAlign: 'right',
                  marginTop: 0.5,
                }}
                primary={formatNumber(value, chooseSymbol(symbol, type))}
              />
            </Stack>
            <Stack direction="row" alignItems="center">
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Fee
                    {gasWarning && (
                      <>
                        <IconButton
                          size="medium"
                          onClick={handleClick}
                          sx={{
                            color: 'error.main',
                            p: 0,
                            ml: 0.5,
                          }}
                        >
                          <CiWarning />
                        </IconButton>
                        <Popover
                          open={!!anchorEl}
                          anchorEl={anchorEl}
                          onClose={handleClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                        >
                          <Box sx={{ p: 2, maxWidth: 300 }}>
                            <Typography>{gasWarning}</Typography>
                          </Box>
                        </Popover>
                      </>
                    )}
                  </Box>
                }
              />
              <ListItemText
                secondary={isLoading ? t('transferEth.loading') : `${fee.slice(0, 7)} ETH`}
                secondaryTypographyProps={{
                  textAlign: 'right',
                }}
              />
            </Stack>

            <Stack direction="row">
              <ListItemText primary={'Type'} />
              <ListItemText
                secondary={capitalize(TransactionType[type])}
                secondaryTypographyProps={{
                  textAlign: 'right',
                  color: (theme) => theme.palette.rainbow.blue,
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </TabPanel>
      <TabPanel value={currentTab} index={1}>
        <Stack px={2} spacing={1}>
          {renderPropertyRow(t('transferEth.params.token'), name)}
          {type === TransactionType.ETH
            ? renderPropertyRow(t('transferEth.params.receiver'), truncateEthAddress(transfer.to))
            : renderPropertyRow(t('transferEth.params.sender'), truncateEthAddress(transfer.to))}
          {contractInformation?.name &&
            renderPropertyRow(t('transferEth.params.function'), contractInformation.name)}
          {contractInformation?.functionType &&
            renderPropertyRow(
              t('transferEth.params.functionType'),
              contractInformation.functionType
            )}
          {contractInformation?.inputs &&
            renderPropertyRow(
              t('transferEth.params.title'),
              contractInformation.inputs.map((input) => `${input.name}: ${input.type}`).join(', ')
            )}
          {transfer.data &&
            transfer.data.length > 2 &&
            renderPropertyRow(t('transferEth.params.hexData'), transfer.data)}
        </Stack>
      </TabPanel>
    </Stack>
  )
}

export default TransferEth
